import AHDate from "_shared/dates/date";
import { AnswerFeedbackCategory, AnswerFeedbackReason } from "ai-assistant/feedback/answer-feedback-reason";
import { AssistantGroundingState } from "ai-assistant/grounding/assistant-grounding-state";
import { z } from "zod";

export type AnswerFeedbackReportJSON = z.infer<typeof AnswerFeedbackReport.AnswerFeedbackReportJSONZ>;

/**
 * One report in a feedback queue, as a super-user reads it (#5204).
 *
 * It identifies the answer - trace id, prompt and tool-schema versions, model, grounding - and does
 * not reproduce it. The question and answer are the reporter's conversation, and this list spans
 * every tenant; the trace id is how an investigator reaches the turn.
 */
export default class AnswerFeedbackReport {
  public static readonly AnswerFeedbackReportJSONZ = z.object({
    id: z.uuid(),
    reason: z.enum(AnswerFeedbackReason),
    category: z.enum(AnswerFeedbackCategory),
    comment: z.string().nullish(),
    reportedAt: z.iso.datetime({ offset: true }),
    traceId: z.string().nullish(),
    answeredAt: z.iso.datetime({ offset: true }),
    legalEntityId: z.uuid(),
    groundingState: z.enum(AssistantGroundingState),
    promptVersion: z.string().nullish(),
    toolSchemaVersion: z.string().nullish(),
    model: z.string().nullish(),
  });

  public static fromJSON(json: AnswerFeedbackReportJSON): AnswerFeedbackReport {
    return new AnswerFeedbackReport(json);
  }

  public readonly id: string;
  public readonly reason: AnswerFeedbackReason;
  public readonly category: AnswerFeedbackCategory;
  public readonly comment: string | undefined;
  public readonly reportedAt: AHDate;
  public readonly traceId: string | undefined;
  public readonly answeredAt: AHDate;
  public readonly legalEntityId: string;
  public readonly groundingState: AssistantGroundingState;
  public readonly promptVersion: string | undefined;
  public readonly toolSchemaVersion: string | undefined;
  public readonly model: string | undefined;

  public constructor(json: AnswerFeedbackReportJSON) {
    this.id = json.id;
    this.reason = json.reason;
    this.category = json.category;
    this.comment = json.comment ?? undefined;
    this.reportedAt = new AHDate(json.reportedAt);
    this.traceId = json.traceId ?? undefined;
    this.answeredAt = new AHDate(json.answeredAt);
    this.legalEntityId = json.legalEntityId;
    this.groundingState = json.groundingState;
    this.promptVersion = json.promptVersion ?? undefined;
    this.toolSchemaVersion = json.toolSchemaVersion ?? undefined;
    this.model = json.model ?? undefined;
  }
}
